import { Button } from "@mui/material";
import React, { useState, useEffect } from "react";
import { MdClose, MdVisibility } from "react-icons/md";
import PreviewFileTable from "./PreviewFileTable";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { getAllImportedHistoryFileData } from "../../../Api";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const tabsData = [
  "OFAC",
  "UKSanctions",
  "NONSDN",
  // "Address",
  "PEP",
  "Watch List",
  "Negative",
];

function ViewFilesTable({ onClose, selectedTab, fileType, addressFileType }) {
  const [modalSelectedFile, setModalSelectedFile] = useState(null);
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const [previewFilesModal, setPreviewFilesModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Reset to first page and clear preview when date filter changes
  useEffect(() => {
    setModalCurrentPage(1);
    setModalSelectedFile(null);
    // Clear the preview section when date changes
  }, [selectedDate]);

  const modalItemsPerPage = 5;
  // Default page size of 5 records
  const navigate = useNavigate();

  // Helper function to get filtered data
  const getFilteredData = () => {
    return selectedDate
      ? tableData[tabsData[selectedTab]]?.filter(
          (row) => row.loadDate === selectedDate
        )
      : tableData[tabsData[selectedTab]];
  };

  const handleClose = () => {
    onClose();
  };

  // here using this api to get the history files in View files Modal
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        Swal.fire({
          html: `<img src=${require("../../../assets/loader.gif")} style="width: 50px" alt="loader">`,
          showConfirmButton: false,
          heightAuto: false,
          customClass: { popup: "loader-swal" },
          allowOutsideClick: false,
        });

        let effectiveFileType;

        if (tabsData[selectedTab] === "Address") {
          effectiveFileType = addressFileType;
          if (!effectiveFileType) {
            setTableData({
              [tabsData[selectedTab]]: [],
            });
            setLoading(false);
            Swal.close();
            return;
          }
        } else {
          const fileTypeMap = {
            OFAC: "OFAC_SDN",
            NONSDN: "OFAC_NON_SDN",
            UKSanctions: "UK_Sanctions",
            "Manual Create": "Manual_Create",
            Negative: "Others",
            PEP: "PEP",
            "Watch List": "WATCH_LIST",
          };
          effectiveFileType = fileTypeMap[tabsData[selectedTab]] || "";
        }

        // Fetch data for current page
        const response = await getAllImportedHistoryFileData({
          file_type: effectiveFileType,
          bank_code: localStorage.getItem("bank_code") || "",
          // page: modalCurrentPage,
          // pageSize: modalItemsPerPage,
        });
        // let response;
        Swal.close();
        if (response.status === "S") {
          const files = response.result_info || [];
          //setFiles(files);
          //   const pagination = response.pagination || {};
          //   // Set total items and pages for pagination
          //   // Force totalItems to 0 if no files are returned, regardless of API pagination info
          //   const actualTotalItems =
          //     files.length === 0 ? 0 : pagination.totalCount || files.length;
          //   setTotalItems(actualTotalItems);
          //   setTotalPages(
          //     actualTotalItems === 0
          //       ? 1
          //       : Math.ceil(actualTotalItems / modalItemsPerPage)
          //   );

          // Extract unique dates for the date filter and convert to DD-MM-YYYY format
          const uniqueDates = Array.from(
            new Set(files.map((f) => dayjs(f.load_date).format("DD-MM-YYYY")))
          );
          setAvailableDates(uniqueDates);

          setTableData({
            [tabsData[selectedTab]]: files.map((file) => ({
              id: `${file.file_name}_${dayjs(file.load_date).format(
                "DD-MM-YYYY"
              )}_${file.load_time}`,
              fileName: file.file_name,
              loadDate: dayjs(file.load_date).format("DD-MM-YYYY"),
              loadTime: file.load_time,
            })),
          });

          const total = files.length;
          setTotalItems(total);
          setTotalPages(Math.ceil(total / modalItemsPerPage) || 1);

          // If no files, ensure we reset to page 1
          if (files.length === 0) {
            setModalCurrentPage(1);
          }

          // Reset selected date if not in new list
          if (selectedDate && !uniqueDates.includes(selectedDate)) {
            setSelectedDate("");
          }
        } else if (
          response.status === "F" &&
          (response.message === "Unauthorized - Missing token" ||
            response.message === "Unauthorized - Invalid token")
        ) {
          Swal.fire({
            text: "Please login with your credentials",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          }).then(() => {
            localStorage.clear();
            navigate("/login");
          });
        } else {
          Swal.fire({
            text: response?.message || "Error fetching files",
            icon: "error",
            showConfirmButton: true,
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        Swal.fire({
          text: "Error fetching files",
          icon: "error",
          showConfirmButton: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    selectedTab,
    fileType,
    navigate,
    addressFileType,
    modalCurrentPage,
    modalItemsPerPage,
  ]); // Added back modalCurrentPage and modalItemsPerPage dependencies

  // Clear preview when tab changes
  useEffect(() => {
    setModalSelectedFile(null);
    setSelectedDate("");
  }, [selectedTab]);

  // Clear preview when addressFileType changes (for Address tab)
  useEffect(() => {
    if (tabsData[selectedTab] === "Address") {
      setModalSelectedFile(null);
      setSelectedDate("");
    }
  }, [addressFileType, selectedTab]);

  // Pagination functions
  const handleModalFirstPage = () => {
    setModalCurrentPage(1);
  };

  const handleModalLastPage = () => {
    setModalCurrentPage(Math.ceil(totalItems / modalItemsPerPage));
  };

  const handleModalPrevPage = () => {
    setModalCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleModalNextPage = () => {
    setModalCurrentPage((prev) =>
      Math.min(prev + 1, Math.ceil(totalItems / modalItemsPerPage))
    );
  };
  const handleModalViewFile = (file) => {
    // Determine the correct file type based on the selected tab
    const effectiveFileType =
      tabsData[selectedTab] === "OFAC"
        ? "OFAC_SDN"
        : tabsData[selectedTab] === "NONSDN"
        ? "OFAC_NON_SDN"
        : tabsData[selectedTab] === "UKSanctions"
        ? "UK_Sanctions"
        : tabsData[selectedTab] === "Manual Create"
        ? "Manual_Create"
        : tabsData[selectedTab] === "Negative"
        ? "Others"
        : tabsData[selectedTab] === "PEP"
        ? "PEP"
        : tabsData[selectedTab] === "Watch List"
        ? "WATCH_LIST"
        : tabsData[selectedTab] === "Address"
        ? "OFAC_ADD_SDN"
        : "OFAC_ADD_NON_SDN";
    // Create the file object with all required properties
    const fileWithType = {
      ...file,
      file_type: effectiveFileType,
      file_name: file.fileName,
      load_date: dayjs(file.loadDate).format("DD-MM-YYYY"), // Use the already formatted date
      load_time: file.loadTime,
    };

    setModalSelectedFile(fileWithType);
  };

  // --- PAGINATION CALCULATION (always correct for current filter) ---
  const filteredData = getFilteredData() || [];
  const filteredCount = filteredData.length;
  const totalFilteredPages =
    filteredCount === 0 ? 1 : Math.ceil(filteredCount / modalItemsPerPage);

  return (
    <div>
      <div className="modal-overlay">
        <div
          className="modal-container bg-white"
          style={{
            maxWidth: "90%",
            width: "90%",
            maxHeight: "90vh",
            height: "auto",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header Section */}
          <div className="row justify-content-center align-items-center bg-white z-index-1">
            <div className="col-12 d-flex justify-content-between align-items-center py-3 border-bottom">
              <h5 className="m-0 px-2">View Files</h5>
              <button
                className="border-0 bg-transparent"
                onClick={() => {
                  handleClose();
                }}
              >
                <MdClose color="black" fontSize={20} />
              </button>
            </div>
          </div>

          {/* Main Content with Single Scroll */}
          <div className="modal-body p-3">
            {/* Table Section (File List) - Always visible */}
            <div className="mb-4">
              <div className="row justify-content-between align-items-center mb-3">
                <div className="col d-flex align-items-center">
                  <h6 className="mb-0">File List</h6>
                </div>
                <div className="col-auto d-flex align-items-center">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Load Date"
                      value={
                        selectedDate ? dayjs(selectedDate, "DD-MM-YYYY") : null
                      }
                      onChange={(date) => {
                        setSelectedDate(date ? date.format("DD-MM-YYYY") : "");
                      }}
                      onOpen={() => {
                        setModalSelectedFile(null); // Hide preview when date picker opens
                      }}
                      format="DD-MM-YYYY"
                      slotProps={{
                        textField: {
                          size: "small",
                          className: "form-control",
                          style: { minWidth: 150 },
                          onClick: () => {
                            setModalSelectedFile(null); // Hide preview when date field is clicked
                          },
                        },
                      }}
                      disableFuture
                      clearable
                    />
                  </LocalizationProvider>
                  {selectedDate && (
                    <Button
                      variant="outlined"
                      size="small"
                      style={{ marginLeft: 8, padding: "8px 10px" }}
                      onClick={() => setSelectedDate("")}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              <div className="table-responsive border rounded bg-white shadow-sm">
                <table className="table table-bordered table-striped mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>#</th>
                      <th>File Name</th>
                      <th>Load Date</th>
                      <th>Load Time</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && getFilteredData()?.length > 0 ? (
                      getFilteredData()
                        .slice(
                          (modalCurrentPage - 1) * modalItemsPerPage,
                          modalCurrentPage * modalItemsPerPage
                        )
                        .map((row, index) => (
                          <tr key={row.id}>
                            <td>
                              {(modalCurrentPage - 1) * modalItemsPerPage +
                                index +
                                1}
                            </td>
                            <td>{row.fileName}</td>
                            <td>{row.loadDate}</td>
                            <td>{row.loadTime}</td>
                            <td className="text-center">
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<MdVisibility />}
                                onClick={() => handleModalViewFile(row)}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          {loading ? "Loading..." : "No data available"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls for File Table */}
              <div className="d-flex justify-content-end align-items-center mt-3">
                <div className="pagination">
                  <div className="pagination">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleModalFirstPage}
                      disabled={modalCurrentPage === 1 || filteredCount === 0}
                      className="me-2"
                    >
                      First
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleModalPrevPage}
                      disabled={modalCurrentPage === 1 || filteredCount === 0}
                      className="me-2"
                    >
                      Prev
                    </Button>

                    <span className="mx-2 align-self-center">
                      Page {modalCurrentPage} of {totalFilteredPages}
                    </span>

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleModalNextPage}
                      disabled={
                        modalCurrentPage >= totalFilteredPages ||
                        totalFilteredPages === 1
                      }
                      className="me-2"
                    >
                      Next
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleModalLastPage}
                      disabled={
                        modalCurrentPage >= totalFilteredPages ||
                        totalFilteredPages === 1
                      }
                    >
                      Last
                    </Button>
                  </div>
                </div>
              </div>

              {/* Preview Section - Appears when a file is selected */}
              {modalSelectedFile && (
                <PreviewFileTable
                  key={`${modalSelectedFile.file_name}_${modalSelectedFile.load_date}_${modalSelectedFile.load_time}`}
                  previewFilesModal={previewFilesModal}
                  selectedFile={modalSelectedFile}
                  onClose={() => {
                    setPreviewFilesModal(false);
                    setModalSelectedFile(null);
                  }}
                  fileType={fileType}
                  selectedTab={selectedTab}
                />
              )}
            </div>
          <div className="d-flex justify-content-end ">
            <button className="btn createButton" onClick={onClose}>
              Close
            </button>
          </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ViewFilesTable;
